import { useState, useEffect, useCallback } from 'react';
import {
  Package, Plus, Pencil, Eye, EyeOff, Loader2, AlertCircle,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button, MessagePlugin } from 'tdesign-react';
import { profileApi,shopApi } from '@/user/api';
import type { ProductVO } from '@/types/api-entity';
import ProductFormModal from '../components/ProductFormModal';

export default function ProductsPage() {
  const [products, setProducts] = useState<ProductVO[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductVO | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const pageSize = 10;
  const totalPages = Math.ceil(total / pageSize);

  const emptyForm: Partial<ProductVO> = {
    productName: '',
    productCode: '',
    category: '',
    description: '',
    price: 0,
    stock: 0,
    unit: '吨',
    mainImage: '',
    status: 0,
  };
  const [currentForm, setCurrentForm] = useState<Partial<ProductVO>>(emptyForm);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await profileApi.getMyProducts({ pageNum: currentPage, pageSize });
      if (res.code === 200 && res.data) {
        setProducts(res.data.list || []);
        setTotal(res.data.total || 0);
      } else {
        setError(res.message || '加载失败');
      }
    } catch (err: any) {
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadProducts();
    shopApi.getProductCategories().then((res) => {
      if (res.code === 200 && res.data) {
        setCategories([...res.data]);
      }
    });
  }, [loadProducts]);

  const handleToggleStatus = async (id: number, currentStatus: number) => {
    const newStatus = currentStatus === 1 ? 2 : 1;
    try {
      const res = await profileApi.toggleMyProductStatus(id, newStatus);
      if (res.code === 200) {
        loadProducts();
      } else {
        setError(res.message || '操作失败');
      }
    } catch (err: any) {
      setError(err.message || '操作失败');
    }
  };

  const handleSaveProduct = async (form: Partial<ProductVO>) => {
    try {
      let res;
      if (editingProduct) {
        res = await profileApi.updateMyProduct(editingProduct.id, form);
      } else {
        res = await profileApi.createMyProduct(form);
      }
      
      if (res.code === 200) {
        MessagePlugin.success(editingProduct ? '更新成功' : '创建成功');
        setShowModal(false);
        loadProducts();
      } else {
        setError(res.message || '操作失败');
      }
    } catch (err: any) {
      setError(err.message || '保存失败');
    }
  };

  const openCreate = () => {
    setEditingProduct(null);
    setCurrentForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (product: ProductVO) => {
    setEditingProduct(product);
    setCurrentForm({
      productName: product.productName,
      productCode: product.productCode,
      category: product.category,
      description: product.description,
      price: product.price,
      stock: product.stock,
      unit: product.unit,
      mainImage: product.mainImage,
      status: product.status,
    });
    setError('');
    setShowModal(true);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getStatusBadge = (status?: number) => {
    switch (status) {
      case 0:
        return <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-xs rounded-full">待审核</span>;
      case 1:
        return <span className="px-2 py-0.5 bg-green-50 text-green-600 text-xs rounded-full">已上架</span>;
      case 2:
        return <span className="px-2 py-0.5 bg-gray-50 text-gray-600 text-xs rounded-full">已下架</span>;
      case 3:
        return <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded-full">审核拒绝</span>;
      default:
        return <span className="px-2 py-0.5 bg-gray-50 text-gray-600 text-xs rounded-full">未知</span>;
    }
  };

  // 【新增】获取第一张图片的 URL
  const getFirstImage = (mainImage: string | string[] | undefined): string => {
    if (!mainImage) return 'https://via.placeholder.com/40';
    
    let firstUrl = '';
    if (Array.isArray(mainImage)) {
      firstUrl = mainImage[0] || '';
    } else if (typeof mainImage === 'string') {
      // 假设是多张图片逗号分隔的情况
      firstUrl = mainImage.split(',')[0].trim();
    }
    
    return firstUrl || 'https://via.placeholder.com/40';
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Package className="w-6 h-6 text-[#1c5bff]" />
          商品管理
        </h1>
        <Button theme="primary" icon={<Plus className="w-4 h-4" />} onClick={openCreate}>
          新增商品
        </Button>
      </div>

      {error && !showModal && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#1c5bff] animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-16 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">暂无商品</p>
          <p className="text-gray-400 text-sm mb-6">点击右上角按钮添加您的第一个商品</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">商品</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">分类</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">价格</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">库存</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">状态</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img
                        // 【修改】使用辅助函数获取第一张图片
                        src={getFirstImage(product.mainImage)}
                        alt={product.productName}
                        className="w-10 h-10 rounded-lg object-cover bg-gray-100"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40';
                        }}
                      />
                      <span className="text-sm font-medium text-gray-900">{product.productName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{product.category || '-'}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatPrice(product.price)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-600">{product.stock} {product.unit || '吨'}</td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(product.status)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(product)}
                        className="p-1.5 text-gray-400 hover:text-[#1c5bff] hover:bg-blue-50 rounded transition-colors"
                        title="编辑"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {product.status === 1 ? (
                        <button
                          onClick={() => handleToggleStatus(product.id, product.status)}
                          className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                          title="下架"
                        >
                          <EyeOff className="w-4 h-4" />
                        </button>
                      ) : product.status === 2 ? (
                        <button
                          onClick={() => handleToggleStatus(product.id, product.status)}
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="上架"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center justify-between p-4 border-t border-gray-100">
            <span className="text-sm text-gray-500">
              共 {total} 件商品，{totalPages} 页
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#1c5bff] hover:border-[#1c5bff] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg font-medium text-sm transition-all ${
                      page === currentPage
                        ? 'bg-[#1c5bff] text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#1c5bff] hover:border-[#1c5bff] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <ProductFormModal
        visible={showModal}
        categories={categories}
        editingProduct={editingProduct}
        initialForm={currentForm}
        onSave={handleSaveProduct}
        onCancel={() => setShowModal(false)}
        error={error}
      />
    </div>
  );
}